import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';

import dayjs, { Dayjs } from 'dayjs';

import { Alert, Box, capitalize, Chip, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DataObjectOutlined, DeviceHubOutlined, ErrorOutline } from '@mui/icons-material';
import {
  GridRenderCellParams,
  GridColDef,
  GridRowParams,
  GridActionsCellItem,
  GridValueGetterParams,
} from '@mui/x-data-grid';

import { Table } from '@common/Table';
import { Button } from '@common/Button';
import { LayoutWorkspace } from '@common/layout';
import { ExecutionData } from '@store/types';
import { getExecutions, getNodeExecutions } from '@requests/reporting';
import { State } from '@store/index';
import { ServiceNavigation } from '@components/Workspace/Navigation';
import { ScrollablePre, ViewError } from '@components/Modals/Logs/ViewError';
import { ViewOutput } from '@components/Modals/Logs/ViewOutput';
import { useTheme } from '../../styles';
import { NodeExecutionSidebar } from './NodeExecutionSidebar';

// Using dynamic export prefix instead of constants

const Container = styled.div`
  display: flex;
  height: calc(100vh - 61px);
`;

const LayoutContainer = styled.div`
  display: flex;
  align-items: flex-start;
  min-height: 0;
  flex: 1;
`;

const LayoutSidebarContainer = styled.div`
  display: flex;
  height: 100%;
  padding: 0;
  width: calc(100vw - 60px - 240px - 50px);
`;

export const Reporting: React.FC = () => {
  const theme = useTheme();

  const { service_id } = useParams<{ service_id: string }>();

  const services = useSelector((state: State) => state.flow.services);
  const service = services.find((service) => service.service_id === service_id);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [logOutput, setLogOutput] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(4, 'week'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(1, 'day'));
  const [runId, setRunId] = useState<string | null>(null);

  const {
    data: executionsList = [],
    isLoading: executionsListLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['reporting', { service_id, startDate: startDate?.toISOString(), endDate: endDate?.toISOString() }],
    queryFn: getExecutions,
    staleTime: 30000,
    refetchOnMount: true,
    refetchOnReconnect: false,
    enabled: Boolean(service_id),
  });

  const { data: nodeExecutions = [], isPending: isNodesLoading } = useQuery({
    queryKey: ['reporting/node-executions', { service_id, run_id: runId }],
    queryFn: getNodeExecutions,
    staleTime: 30000,
    refetchOnMount: true,
    refetchOnReconnect: false,
    enabled: Boolean(service_id && runId),
  });

  // Row click handler for the Table component
  const handleRowClick = (row: any) => {
    // Access the run_id from row.id which is set by getRowId
    setRunId(row.id);
    setDrawerOpen(true);
  };

  const getChipColor = (status: string) => {
    if (status === 'completed') return 'success';
    if (status === 'failed') return 'error';
    if (status === 'pending') return 'warning';
    return undefined;
  };

  let errorMessage = '';
  if (axios.isAxiosError(error)) errorMessage = error.response?.data?.error || error.message;
  else errorMessage = error?.message || '';

  const columns: GridColDef<ExecutionData>[] = [
    {
      field: 'run_id',
      headerName: 'Run ID',
      flex: 40,
      sortable: true,
      renderCell: (params: GridRenderCellParams<ExecutionData>) => {
        const { row } = params;
        return <ScrollablePre>{row.run_id}</ScrollablePre>;
      },
    },
    {
      field: 'name',
      headerName: 'Workflow',
      flex: 50,
      renderCell: (params: GridRenderCellParams<ExecutionData>) => {
        const { row } = params;
        return (
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <DeviceHubOutlined sx={{ color: theme.text.subtext, fontSize: '1.8ch !important' }} />
            <div>{row.name}</div>
          </Stack>
        );
      },
    },
    {
      field: 'user_id',
      headerName: 'User ID',
      flex: 40,
      sortable: true,
      renderCell: (params: GridRenderCellParams<ExecutionData>) => {
        const { row } = params;
        return (
          <a
            href={`https://admin.telnyx.com/users/user/${row.user_id}`}
            target='_blank'
            referrerPolicy='no-referrer'
            rel='noreferrer'
          >
            {row.user_id}
          </a>
        );
      },
    },
    {
      field: 'org_id',
      headerName: 'Organization ID',
      flex: 40,
      sortable: true,
      renderCell: (params: GridRenderCellParams<ExecutionData>) => {
        const { row } = params;
        return (
          <a
            href={`https://admin.telnyx.com/users/user/${row.org_id}`}
            target='_blank'
            referrerPolicy='no-referrer'
            rel='noreferrer'
          >
            {row.org_id}
          </a>
        );
      },
    },
    {
      field: 'service_id',
      headerName: 'Service ID',
      flex: 40,
      sortable: true,
      renderCell: (params: GridRenderCellParams<ExecutionData>) => {
        const { row } = params;
        return <ScrollablePre>{row.service_id}</ScrollablePre>;
      },
    },
    {
      field: 'flow_id',
      headerName: 'Flow ID',
      flex: 40,
      sortable: true,
      renderCell: (params: GridRenderCellParams<ExecutionData>) => {
        const { row } = params;
        return <ScrollablePre>{row.flow_id}</ScrollablePre>;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 22,
      type: 'string',
      sortable: true,
      renderCell: (params: GridRenderCellParams<ExecutionData>) => {
        const { row } = params;
        return (
          <Chip
            color={getChipColor(row.status)}
            label={capitalize(row.status)}
            size='small'
            variant='outlined'
            sx={{ fontSize: '1.3ch', height: '19px !important' }}
          />
        );
      },
    },
    {
      field: 'platform',
      headerName: 'Platform',
      flex: 18,
      type: 'string',
      sortable: true,
    },
    {
      field: 'duration',
      type: 'string',
      headerName: 'Duration',
      flex: 20,
      sortable: true,
    },
    {
      field: 'executed_nodes',
      headerName: 'Executed Nodes',
      flex: 20,
      sortable: true,
      renderCell: (params: GridRenderCellParams<ExecutionData>) => {
        const { row } = params;
        return `${row.executed_nodes}/${row.total_nodes}`;
      },
    },
    {
      field: 'output',
      headerName: 'Output',
      type: 'actions',
      flex: 15,
      sortable: true,
      getActions: (params: GridRowParams<ExecutionData>) => {
        const { row } = params;

        const disabled = row?.output === 'null' || row?.error !== null;

        return [
          <GridActionsCellItem
            key='output'
            icon={<DataObjectOutlined sx={{ color: !disabled ? theme.primary : undefined }} />}
            label={row?.output === null ? 'No Output' : 'View Output'}
            disabled={disabled}
            onClick={() => {
              setLogOutput(row?.output ?? 'There was no output information stored for this execution.');
            }}
          />,
        ];
      },
    },
    {
      field: 'error',
      headerName: 'Error',
      type: 'actions',
      flex: 18,
      sortable: true,
      getActions: (params: GridRowParams<ExecutionData>) => {
        const { row } = params;
        return [
          <GridActionsCellItem
            key='error'
            icon={<ErrorOutline />}
            sx={{ color: 'error.main' }}
            label={row?.error === null || row?.error === '' ? 'No Error' : 'View Error'}
            disabled={row?.error === null || row?.error === ''}
            onClick={() => {
              setLogError(row?.error ?? 'There was no error information stored for this log.');
            }}
          />,
        ];
      },
    },
    {
      field: 'error_code',
      headerName: 'Error Code',
      flex: 25,
      type: 'string',
      sortable: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 40,
      sortable: true,
      renderCell: (params: GridRenderCellParams<ExecutionData>) => {
        const { row } = params;
        return <ScrollablePre>{row.email}</ScrollablePre>;
      },
    },
    {
      field: 'created_at',
      type: 'dateTime',
      headerName: 'Created At (UTC)',
      flex: 40,
      sortable: true,
      valueGetter: (params: GridValueGetterParams<ExecutionData>) => {
        const { value } = params;
        return new Date(value);
      },
    },
  ];

  const body = (
    <LayoutSidebarContainer>
      <ViewError error={logError} setError={setLogError} />
      <ViewOutput output={logOutput} setOutput={setLogOutput} />
      {error ? (
        <Box sx={{ padding: 2 }}>
          <Alert
            severity='error'
            action={
              <Button customColor='red' onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            {errorMessage}
          </Alert>
        </Box>
      ) : (
        <Table
          data={executionsList ?? []}
          getRowId={(row) => row.run_id}
          onRowClick={handleRowClick}
          columns={columns}
          loading={executionsListLoading}
          density='compact'
          checkboxes={true}
          height='auto'
          dateRange={{
            startDate,
            endDate,
            onStartDateChange: (date) => setStartDate(date),
            onEndDateChange: (date) => setEndDate(date),
          }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                user_id: false,
                org_id: false,
                service_id: false,
                error_code: false,
                run_id: false,
                flow_id: false,
              },
            },
          }}
        />
      )}
      <NodeExecutionSidebar
        traces={nodeExecutions}
        open={drawerOpen}
        setOpen={setDrawerOpen}
        runId={runId}
        isNodesLoading={isNodesLoading}
        executionsList={executionsList}
      />
    </LayoutSidebarContainer>
  );

  return (
    <LayoutWorkspace
      header={service?.service_name || 'Workspace'}
      body={
        <LayoutContainer>
          <ServiceNavigation />
          <Container>{body}</Container>
        </LayoutContainer>
      }
    />
  );
};
